import {
  Button,
  ButtonGroup,
  DeleteIcon,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  EditIcon,
  Table,
  type TableColumns,
} from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '../../../hooks/useIsDesktop';
import { MyAbsenceContext } from '../types/absenceTypes';

const MyAbsencesTableFragment = graphql(`
  fragment MyAbsencesTableFields on Query {
    absences(where: $absencesWhere, options: { sort: [{ startTime: DESC }] }) {
      uuid
      comment
      created
      excused
      owner {
        __typename
        uuid
      }
      reason {
        __typename
        uuid
        reason
        internal
      }
      startTime
      endTime
      updated
      days {
        __typename
        uuid
      }
    }
  }
`);

type MyAbsencesTableProps = {
  isLoading: boolean;
  onAdd: (context: MyAbsenceContext) => void;
  onEdit: (uuid: string, context: MyAbsenceContext) => void;
  onExcuse: (uuid: string, isExcused: boolean) => void;
  onDelete: (uuid: string) => void;
  data: FragmentType<typeof MyAbsencesTableFragment> | undefined;
};

type MyAbsencesTableType = {
  uuid: string;
  name: string;
  reason: string;
  from: Date;
  until: Date;
  comment: string;
  isExcused: boolean;
  ownerUuid: string;
  context: MyAbsenceContext;
};

export const MyAbsencesTable = ({ isLoading, onAdd, onEdit, onExcuse, onDelete, data }: MyAbsencesTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const profile = pimAuthClaims.getProfile();
  const children = pimAuthClaims.getChildProfiles();

  const fragment = getFragmentData(MyAbsencesTableFragment, data);
  const permissions = usePermissionChecker();

  const canCreateForSelf = permissions?.canCreateAbsenceForSelf();
  const canCreateForChild = permissions?.canCreateAbsenceForChildren();
  const canCreate = canCreateForSelf || canCreateForChild;
  const isDesktop = useIsDesktop();

  const tableData: MyAbsencesTableType[] =
    fragment?.absences.map((absence) => {
      const child = children.find((child) => child.uuid === absence.owner.uuid);

      return {
        uuid: absence.uuid,
        name: child ? (child.displayName ?? '') : (profile.displayName ?? ''),
        reason: absence.reason?.reason ?? '',
        from: dayjs(absence.startTime).toDate(),
        until: dayjs(absence.endTime).toDate(),
        comment: absence.comment ?? '',
        isExcused: absence.excused ?? false,
        ownerUuid: absence.owner.uuid,
        context: child ? 'child' : 'myself',
      };
    }) ?? [];

  const columns: TableColumns<MyAbsencesTableType>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('common.name'),
      size: 175,
    },
    {
      id: 'reason',
      accessorKey: 'reason',
      header: t('absences.reasons.title', { context: 'short' }),
      size: 250,
    },
    {
      id: 'from',
      accessorKey: 'from',
      header: t('common.from'),
      size: 135,
      accessorFn: (row) => dayjs(row.from).format('DD.MM.YY - HH:mm'),
    },
    {
      id: 'until',
      accessorKey: 'until',
      header: t('common.until'),
      size: 135,
      accessorFn: (row) =>
        dayjs(row.from).isSame(row.until, 'day')
          ? dayjs(row.until).format('HH:mm')
          : dayjs(row.until).format('DD.MM.YYYY - HH:mm'),
    },
    {
      id: 'comment',
      accessorKey: 'comment',
      header: t('common.comment'),
      size: 250,
    },
    {
      id: 'isExcused',
      accessorKey: 'isExcused',
      header: t('absences.isExcused'),
      type: 'boolean',
      size: 75,
    },
  ];

  return (
    <Table<MyAbsencesTableType>
      data={tableData}
      columns={columns}
      loading={isLoading}
      showBorderRadius
      showShadow
      isDesktop={isDesktop}
      isOnWhite={false}
      showActionBar
      actionBarSettings={{
        showAddButton: canCreate,
      }}
      onAddClick={() => {
        if (canCreateForSelf) onAdd('myself');
        if (canCreateForChild) onAdd('child');
      }}
      lastColWidth='68px'
      lastCol={({ original }) => {
        const uuid = original.uuid;
        const context = original.context;
        const start = dayjs(original.from).toDate();
        const end = dayjs(original.until).toDate();

        const isStudentCanUpdate = context === 'myself' && !!permissions?.canUpdateAbsenceForSelf({ start });
        const isStudentCanDelete = context === 'myself' && !!permissions?.canDeleteAbsenceForSelf({ start });
        const isParentCanUpdate = context === 'child' && !!permissions?.canUpdateAbsenceForChildren({ end });
        const isParentCanDelete = context === 'child' && !!permissions?.canDeleteAbsenceForChildren({ start });
        const isParentCanExcuse = context === 'child' && !!permissions?.canExcuseChildren();

        return context === 'myself' ? (
          <ButtonGroup disabled={!isStudentCanUpdate && !isStudentCanDelete}>
            <Button
              disabled={!isStudentCanUpdate}
              hierarchy='tertiary'
              onClick={() => onEdit(uuid, context)}
              icon={<EditIcon className='small' />}
            />
            <Button
              disabled={!isStudentCanDelete}
              hierarchy='tertiary'
              onClick={() => onDelete(uuid)}
              icon={<DeleteIcon className='small' color='var(--color-error)' />}
            />
          </ButtonGroup>
        ) : (
          <Dropdown trigger={<Button icon={<DotsVerticalIcon />} hierarchy='ghost' />}>
            <DropdownMenu
              data={[
                {
                  label: t('common.edit'),
                  onClick: () => onEdit(original.uuid, original.context),
                  disabled: !isParentCanUpdate,
                },
                {
                  label: original.isExcused ? t('absences.cancelExcuse') : t('absences.setExcuse'),
                  onClick: () => onExcuse(original.uuid, !original.isExcused),
                  disabled: !isParentCanExcuse,
                },
                {
                  type: 'ruler',
                },
                {
                  label: t('common.delete'),
                  color: 'error',
                  onClick: () => onDelete(original.uuid),
                  disabled: !isParentCanDelete,
                },
              ]}
            />
          </Dropdown>
        );
      }}
    />
  );
};
